import { useEffect, useState } from 'react';
import type { FleetHostHealthSeries } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';

type State = {
  readonly machineId: string;
  readonly status: 'loading' | 'ready' | 'failed';
  readonly series: FleetHostHealthSeries | null;
};

/**
 * One machine's readings over [from, to). A refetch for the same machine keeps what is shown until the new series
 * lands, and a failed refetch keeps it too: only a machine with nothing to show reads as failed. A response for an
 * older range or machine is dropped.
 */
export function useHostHealthSeries(machineId: string, from: Date, to: Date): Pick<State, 'status' | 'series'> {
  const [state, setState] = useState<State>({ machineId, status: 'loading', series: null });
  const fromMs = from.getTime();
  const toMs = to.getTime();

  useEffect(() => {
    let cancelled = false;

    const failed = (previous: State): State =>
      previous.machineId === machineId && previous.series !== null
        ? previous
        : { machineId, status: 'failed', series: null };

    void (async () => {
      try {
        const result = await apiClient.adminFleetMachines.health({
          pathParams: { machineId },
          query: { from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString() },
        });

        if (cancelled) return;

        setState((previous) =>
          result.ok ? { machineId, status: 'ready', series: result.body.data } : failed(previous),
        );
      } catch {
        if (!cancelled) setState(failed);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [machineId, fromMs, toMs]);

  return state.machineId === machineId
    ? { status: state.status, series: state.series }
    : { status: 'loading', series: null };
}
